import { titleize } from 'inflected';
import {
  ActivityKind,
  ConfidenceBand,
  EventTypes,
  Importance,
  SignalKind,
  SignalTag,
  SourceKind
} from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { APP_2_CHANNELS, getChannelPrettyName } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { isOneOf } from '@blisspointmedia/bpm-types/dist/typeGuards';
import { SortBy } from '../activity-feed-constants';
import { EventTheme, EventThemes } from '../oracle-taxonomy-constants';

// Static dropdown options for the Channel filter — matches the shape used elsewhere in the
// repo (e.g. SlideGeneratorAdmin Blueprints) so labels stay consistent across channel pickers.
export const CHANNEL_OPTIONS = [...APP_2_CHANNELS]
  .sort((a, b) => a.localeCompare(b))
  .map(ch => ({ label: getChannelPrettyName(ch), value: ch }));

export const EVENT_TYPE_OPTIONS = EventTypes.map(eventType => ({
  label: eventType,
  value: eventType
}));

// --- Types ---

export const DatePreset = {
  TODAY: 'today',
  LAST_7: 'last_7',
  LAST_30: 'last_30',
  UPCOMING: 'upcoming',
  CUSTOM: 'custom'
} as const;
export type DatePreset = (typeof DatePreset)[keyof typeof DatePreset];
export const isDatePreset = isOneOf(Object.values(DatePreset));

export interface ActivityFeedFilterState {
  clients: string[];
  date: DatePreset | null;
  customStartDate: string | null;
  customEndDate: string | null;
  importance: Importance[];
  signals: SignalKind[];
  categories: EventTheme[];
  channels: string[];
  // ActivityKinds to show in the feed. An absent URL param reads as the default;
  // toggling every kind off leaves an empty array (show nothing).
  kinds: ActivityKind[];
}

// The kinds exposed as toggles in the sidebar, in display order, with their plural row labels.
// bp_alert has no toggle.
export const KIND_TOGGLE_ROWS = [
  { value: ActivityKind.EVENT, label: 'Events' },
  { value: ActivityKind.MEETING, label: 'Meetings' },
  { value: ActivityKind.TASK, label: 'Tasks' }
] as const;

export const DEFAULT_FILTER_STATE: ActivityFeedFilterState = {
  clients: [],
  date: null,
  customStartDate: null,
  customEndDate: null,
  importance: [],
  signals: [],
  categories: [],
  channels: [],
  kinds: [ActivityKind.EVENT]
};

// --- Internal constants used by filter/sort logic ---

export const IMPORTANCE_ORDER: Record<Importance, number> = {
  [Importance.HIGH]: 0,
  [Importance.MEDIUM]: 1,
  [Importance.LOW]: 2,
  [Importance.NONE]: 3
};

// --- UI option lists ---

const CURRENT_YEAR = new Date().getFullYear();
export const CALENDAR_YEARS = Array.from({ length: CURRENT_YEAR - 2020 + 1 }, (_, i) => 2020 + i);

export const ALL_VALUE = '__all__';

export const SORT_OPTIONS: { label: string; value: SortBy }[] = [
  { label: 'Most recent', value: SortBy.MOST_RECENT },
  { label: 'Priority', value: SortBy.PRIORITY }
];

export const IMPORTANCE_OPTIONS: { label: string; value: Importance }[] = [
  { label: 'High', value: Importance.HIGH },
  { label: 'Medium', value: Importance.MEDIUM },
  { label: 'Low', value: Importance.LOW },
  { label: 'N/A', value: Importance.NONE }
];

export const SIGNAL_OPTIONS: { label: string; value: SignalKind }[] = [
  { label: 'Risk', value: SignalKind.RISK },
  { label: 'Positive', value: SignalKind.POSITIVE }
];

export const DATE_OPTIONS: { label: string; value: DatePreset }[] = [
  { label: 'Today', value: DatePreset.TODAY },
  { label: 'Last 7 days', value: DatePreset.LAST_7 },
  { label: 'Last 30 days', value: DatePreset.LAST_30 },
  { label: 'Upcoming', value: DatePreset.UPCOMING },
  { label: 'Custom Range', value: DatePreset.CUSTOM }
];

export const THEME_OPTIONS = EventThemes.map(theme => ({
  label: theme,
  value: theme
}));

// --- Stubbed option lists for the AdvancedFilters section ---

export const SOURCE_OPTIONS = Object.values(SourceKind).map(v => ({
  label: titleize(v),
  value: v
}));

export const CONFIDENCE_OPTIONS = Object.values(ConfidenceBand).map(v => ({
  label: titleize(v),
  value: v
}));

// Named-constant refs — a taxonomy rename in bpm-types will fail to compile here.
export const RISK_TYPE_OPTIONS: { label: string; value: SignalTag }[] = [
  { label: 'Relationship', value: SignalTag.RELATIONSHIP_RISK },
  { label: 'Performance / Business', value: SignalTag.PERFORMANCE_BUSINESS_RISK }
];
